import * as React from 'react';
import styled from 'styled-components';
import { space, SpaceProps } from 'styled-system';
import { useRouter } from 'next/router';

import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { SideEffectsContext, PageContext, useABTestingConfig } from '@packages/shared';
import { CmsContentType, CmsModuleType, Page, ImageCDNProvider } from '@packages/common';
import { is, isNot } from '@packages/ui';

import type { SearchProps } from './Search/Search';
import { DYContainer, DYSliderContainer } from './DYContainer/DYContainer';

import { Carousel } from './Carousel/Carousel';
import { SocialMedia } from './SocialMedia/SocialMedia';
import { ImageBanner } from './ImageBanner/ImageBanner';
import { Teaser } from './Teaser/Teaser';
import { Text } from './Text/Text';
import { Ad } from './Ad/Ad';
import { Products } from './Products/Products';
import { Row } from './Row/Row';
import { Headline } from './Headline/Headline';
import { Error } from './Error/Error';
import { SocialEmbed } from './SocialEmbed/SocialEmbed';
import { TagTeaser } from './TagTeaser/TagTeaser';
import { Newsletter } from './Newsletter/Newsletter';
import { Categories } from './Categories/Categories';
import JumpToSectionAnchor from './JumpToSectionAnchor/JumpToSectionAnchor';
import { DyCampaign } from './DyCampaign/DyCampaign';
import { Search } from './Search/Search';
import { UcPrivacyPolicy } from './UcPrivacyPolicy/UcPrivacyPolicy';

const Container = styled.section<SpaceProps & { isRoot: boolean; spaceOnLastChild: boolean }>`
  ${isNot('spaceOnLastChild')`
    ${space};
  `}
  ${is('isRoot')`
    content-visibility: auto;
  `}
  ${is('spaceOnLastChild')`
     &>*:last-child {
       ${space};
     }
  `}
`;

const MARGIN_BOTTOM = [3, 3, 5, 5, 6];
const ROUTES = getPublicRuntimeConfig('ROUTES');

export const SearchSubmitMixin: React.FC<Omit<SearchProps, 'onSubmit'>> = (props) => {
  const { push } = useRouter();
  const { searchSubmit } = React.useContext(SideEffectsContext);
  // TODO do we need searchhub integration here?
  const handleSubmit = React.useCallback(
    ({ query }: { query: string }) => {
      searchSubmit(query);
      push(`${ROUTES.search}?query=${query}`);
    },
    [push, searchSubmit],
  );
  return <Search {...props} onSubmit={handleSubmit}></Search>;
};

const typeMap: { [key in CmsModuleType]: React.ComponentType<any> | undefined } = {
  [CmsModuleType.PRODUCTS]: Products,
  [CmsModuleType.NEWSLETTER]: Newsletter,
  [CmsModuleType.CATEGORIES]: Categories,
  [CmsModuleType.TEASER]: Teaser,
  [CmsModuleType.CAROUSEL]: Carousel,
  [CmsModuleType.SEARCH]: SearchSubmitMixin,
  [CmsModuleType.SOCIAL_MEDIA]: SocialMedia,
  [CmsModuleType.IMAGE_BANNER]: ImageBanner,
  [CmsModuleType.TEXT]: Text,
  [CmsModuleType.AD]: Ad,
  [CmsModuleType.ROW]: Row,
  [CmsModuleType.HEADLINE]: Headline,
  [CmsModuleType.ERROR]: Error,
  [CmsModuleType.SOCIAL_EMBED]: SocialEmbed,
  [CmsModuleType.TAG_TEASER]: TagTeaser,
  [CmsModuleType.SLOT]: () => null,
  [CmsModuleType.REDIRECT]: () => null,
  [CmsModuleType.PRODUCTS_V2]: Products,
  [CmsModuleType.JUMP_TO_SECTION]: JumpToSectionAnchor,
  [CmsModuleType.DY_CONTAINER]: DYContainer,
  [CmsModuleType.DY_SLIDER_CONTAINER]: DYSliderContainer,
  [CmsModuleType.DY_CAMPAIGN]: DyCampaign,
  [CmsModuleType.UC_PRIVACY_POLICY]: UcPrivacyPolicy,
};

export type CmsModuleProps = {
  name?: string;
  contentType?: CmsContentType;
  space?: SpaceProps;
  isRoot?: boolean;
};

const defaultProps = ['name', 'id', 'contentType', 'title'];

const isNotEmptyComponent = (props: any) => {
  return Object.keys(props).some((item) => !defaultProps.includes(item));
};

export const CmsModule: React.FC<CmsModuleProps> = ({ space = {}, isRoot = false, ...props }) => {
  const { contentType, name = '' } = props;
  const { pageType } = React.useContext(PageContext);
  const { IMAGE_CDN_PROVIDER } = useABTestingConfig() || {};
  const SectionComponent = typeMap[contentType as CmsModuleType];

  if (SectionComponent) {
    const isRow = contentType === CmsModuleType.ROW;
    const isDySlider = contentType === CmsModuleType.DY_SLIDER_CONTAINER;
    if (isNotEmptyComponent(props) && !(isDySlider || isRow)) {
      // if no margin is explicitly passed, set a default bottom margin
      if (!space.mb) {
        space.mb = MARGIN_BOTTOM;
      }
    }

    const otherProps: {
      cdnProvider?: ImageCDNProvider;
      moduleComponent?: React.FC<CmsModuleProps>;
    } = {
      cdnProvider: IMAGE_CDN_PROVIDER,
      moduleComponent: isRow ? CmsModule : undefined,
    };
    // DY API campaigns can be rendered empty, so their last child component should have
    // bottom margin IF it appears
    const isDyApiCampaign = contentType === CmsModuleType.DY_CAMPAIGN;
    return (
      <Container
        {...space}
        spaceOnLastChild={isDyApiCampaign}
        isRoot={pageType === Page.contentful && isRoot}
        data-cms={contentType}
        data-testid={`cms-module${name ? ':' : ''}${name}`}
      >
        <SectionComponent {...props} {...otherProps} />
      </Container>
    );
  }

  return null;
};
